import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
} from "@fluentui/react-components";
import { DocumentTableRegular } from "@fluentui/react-icons";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useBankStatementsStore } from "store/BankStatementsStore";
import { toast } from "components/FluentToast";
import { VSpace } from "components/Spacer";
import { PerfectMatchExportExcel } from "./PerfectMatchExportExcel";

export const PerfectMatchDialog = () => {
  const [
    {
      perfectMatchDialog,
      perfectMatchStatus,
      perfectMatchResults,
      bankStatementEntries,
      bankStatementEntryMetadataMap,
    },
    {
      setBankStatementsStoreValues,
      perfectReminderMatch,
    },
  ] = useBankStatementsStore();

  const { t } = useTranslation();
  const refExcelExport = useRef<ExcelExport>();

  const isLoading = perfectMatchStatus === "loading";
  const isSuccess = perfectMatchStatus === "success";

  const handleConfirm = () => {
    perfectReminderMatch(
      { bankStatementId: perfectMatchDialog.bankStatementId },
      (results) => {
        toast.success(t("greco.success"));
      }
    );
  };

  const handleClose = () => {
    const hasResults = isSuccess && perfectMatchResults.length > 0;

    const storeUpdate: any = {
      perfectMatchDialog: { isOpen: false, bankStatementId: null },
      perfectMatchStatus: "none",
      perfectMatchResults: [],
    };

    if (hasResults) {
      const updatedEntryIds = new Set(
        perfectMatchResults.map((r) => r.bankStatementEntryId)
      );

      storeUpdate.bankStatementEntries = bankStatementEntries.map((entry) => {
        const updated = perfectMatchResults.find(
          (r) => r.bankStatementEntryId === entry.bankStatementEntryId
        );
        // every returned entry was just cleared by perfect match
        return updated
          ? { ...updated, isCleared: true, isPerfectMatchClearance: true }
          : entry;
      });

      const newMetadataMap = { ...bankStatementEntryMetadataMap };
      updatedEntryIds.forEach((entryId) => {
        if (newMetadataMap[entryId]) {
          newMetadataMap[entryId] = {
            ...newMetadataMap[entryId],
            cleared: true,
            editable: false,
            highlighted: true,
          };
        }
      });
      storeUpdate.bankStatementEntryMetadataMap = newMetadataMap;
    }

    setBankStatementsStoreValues(storeUpdate);
  };

  const saveExcel = (component) => {
    if (!component) return;
    const options = component.workbookOptions();
    component.save(options);
  };

  return (
    <>
      <Dialog open={perfectMatchDialog.isOpen}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {t("pct.clearAllPerfectMatches.label")}
            </DialogTitle>
            <DialogContent>
              {isLoading ? (
                <>
                  <VSpace height={50} />
                  <Spinner size="large" />
                  <VSpace height={50} />
                </>
              ) : isSuccess ? (
                <p>
                  {t("pct.clearAllPerfectMatchesResult.label", {
                    count: perfectMatchResults.length,
                  })}
                </p>
              ) : (
                <p>{t("pct.clearAllPerfectMatchesDescription.label")}</p>
              )}
            </DialogContent>
            <DialogActions>
              {isSuccess && perfectMatchResults.length > 0 ? (
                <Button
                  appearance="primary"
                  icon={<DocumentTableRegular />}
                  onClick={() => {
                    saveExcel(refExcelExport.current);
                  }}
                >
                  {t("greco.exportExcel")}
                </Button>
              ) : null}
              {!isSuccess ? (
                <Button
                  data-help="bank-statements-perfect-match-confirm"
                  appearance="primary"
                  disabled={isLoading}
                  onClick={handleConfirm}
                >
                  {t("pct.confirm.label")}
                </Button>
              ) : null}
              <Button
                appearance="secondary"
                disabled={isLoading}
                onClick={handleClose}
              >
                {t("pct.close.label")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <PerfectMatchExportExcel ref={refExcelExport} />
    </>
  );
};
